import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';

/**
* AI:
* Status: 'in progress'
* Type: Service
* SubType: WidgetConfig
* Reason: Configured data provider for widget data fetching
*/

import {WidgetData} from 'features/core/shared/widget/widget-data';
import {WidgetFactory} from 'features/core/shared/widget/service/widget.factory';
import {FilterCriteria} from 'features/core/shared/filter/api/filter.criteria';
import {Sorting} from 'features/core/shared/misc/sorting';
import {ObjectIdentifier} from 'features/core/shared/basic/object-identifier';
import {ObjectIdentifierData} from 'features/core/shared/basic/object-identifier-data';
import {MvsObjectNavigationService} from 'features/core/shared/navigation/mvs-object-navigation.service';
import {MvsObjectNavigationEntry} from 'features/core/shared/navigation/mvs-object-navigation-entry';
import {MvsObjectNavigationActionEnum} from 'features/core/shared/navigation/mvs-object-navigation-action-enum';
import {Subscription} from 'rxjs';
import {CnDqCountersDto} from '../../../model/dto/api/cn-dq-counters.dto';
import {CnReconciliationApiService} from '../../../service/api/cn-reconciliation-api/cn-reconciliation-api.service';

/**
* Data-quality panel (FR-15, Datenqualität config nav section).
*
* Renders a SERVER-PAGINATED list of CommissionExpectation rows with
* status IN (INCOMPLETE_BASE, UNMATCHED) using an mvs-widget QL table
* (NFR-Perf-1: no client-side full materialisation at 50k scale).
*
* Columns include incompleteReason and missingField; the coverage quote
* ('48.757/50.000 = 97,5 %') is shown prominently as a KPI tile.
* Clicking a row jumps to the related cm.Contract via the navigation service.
*
* Status filter buttons (Alle / Unvollständige Basis / Nicht zugeordnet) rebuild
* the widget with a fixed filter; the widget alias stays identical across states.
* Status colors are rendered by mvs-widget from the backend enum uiColor — never hardcoded.
*
* NFR-CleanCore: the FE performs no calculation; the coverage counters come from the backend.
*/
@Component({
    selector: 'mvs-cn-data-quality-panel',
    templateUrl: './cn-data-quality-panel.component.html',
    styleUrls: ['./cn-data-quality-panel.component.scss'],
    standalone: false
})
export class CnDataQualityPanelComponent implements OnInit, OnChanges, OnDestroy {

    /** Identical widget alias across all filter states (NFR-Perf-1 / consistency). */
    static readonly WIDGET_ALIAS = 'cn.config.data.quality.expectation.page';

    /**
     * Backend-supplied coverage / DQ counters. When the host provides them (e.g. from a simulation
     * result) they take precedence; otherwise the panel fetches the GLOBAL counters on init so the
     * KPI tiles always show real numbers instead of "Keine Abdeckungsdaten".
     */
    @Input() counters: CnDqCountersDto | null;

    /** Optional run scope: when set, the panel only lists this run's expectations. */
    @Input() simulationRunId?: number;

    /**
     * Optional Periode facet ({@code YYYY-MM}) from the dashboard's global filter (TM-1000001915 (b)).
     * When set (and no host-supplied counters), the self-fetched DQ counters are scoped to this period
     * so the card matches the rest of the cockpit instead of showing global numbers.
     */
    @Input() period?: string | null;

    /**
     * Optional Gesellschaft facet (partner id) from the dashboard's global filter (TM-1000001915 (b)).
     * Scopes the self-fetched DQ counters to a single partner; null/undefined = all partners.
     */
    @Input() partnerId?: number | null;

    /** Loading flag controlled by the host while counters are being fetched. */
    @Input() loading: boolean = false;

    /** Optional error message; when set an error state is shown. */
    @Input() errorMessage: string | null = null;

    expectationWidget: WidgetData;
    activeStatusFilter: 'ALL' | 'INCOMPLETE_BASE' | 'UNMATCHED' = 'ALL';
    initialized: boolean = false;

    /** Subscription for the global DQ-counter fetch (cleaned up on destroy). */
    private countersSubscription?: Subscription;

    /**
     * Whether THIS panel owns its counters (i.e. self-fetches them) rather than receiving per-simulation
     * counters from the host. Captured once at init from the absence of a bound {@link counters} input —
     * only a self-fetching panel reacts to the dashboard's Periode/Gesellschaft filter (TM-1000001915 (b)).
     */
    private selfFetchesCounters: boolean = false;

    constructor(
        private navigationService: MvsObjectNavigationService,
        private reconciliationApiService: CnReconciliationApiService
    ) {
    }

    ngOnInit(): void {
        this.initComponent();
    }

    /**
    * Initialize the panel: build the server-paged expectation widget once and, when the host did
    * not supply per-simulation counters, fetch the GLOBAL DQ counters so the KPI tiles render real
    * numbers instead of the empty "Keine Abdeckungsdaten" state.
    */
    initComponent(): void {
        this.refreshExpectationWidget();
        // A panel without host-supplied counters owns its counters and reacts to the global filter.
        this.selfFetchesCounters = !this.counters;
        if (this.selfFetchesCounters) {
            this.loadGlobalCounters();
        }
        this.initialized = true;
    }

    /**
    * Fetch the DQ counters (coverage covered/total, incompleteBaseCount, unmatchedCount) from the
    * backend and bind them to the KPI tiles. When the dashboard supplies the global Periode/Gesellschaft
    * filter (TM-1000001915 (b)), the counters are scoped to those facets so the card matches the rest of
    * the cockpit; otherwise the unfiltered global counters are fetched. The FE performs no calculation —
    * the counters come straight from the backend (NFR-CleanCore).
    */
    loadGlobalCounters(): void {
        this.loading = true;
        this.errorMessage = null;
        this.countersSubscription?.unsubscribe();
        this.countersSubscription = this.reconciliationApiService
            .getGlobalDqCounters(this.period ?? undefined, this.partnerId ?? undefined)
            .subscribe({
                next: (counters: CnDqCountersDto) => {
                    this.counters = counters;
                    this.loading = false;
                },
                error: () => {
                    this.errorMessage = 'Abdeckungsdaten konnten nicht geladen werden.';
                    this.loading = false;
                }
            });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
        if (changes['simulationRunId']) {
            this.refreshExpectationWidget();
        }
        // TM-1000001915 (b): re-scope the self-fetched counters when the dashboard's global Periode /
        // Gesellschaft filter changes. Only a self-fetching panel reacts — when the host feeds in
        // per-simulation counters they always take precedence and are never overwritten.
        if (this.selfFetchesCounters && (changes['period'] || changes['partnerId'])) {
            this.loadGlobalCounters();
        }
    }

    ngOnDestroy(): void {
        this.countersSubscription?.unsubscribe();
    }

    /**
    * Switch the status filter and rebuild the server-paged widget.
    */
    setStatusFilter(filter: 'ALL' | 'INCOMPLETE_BASE' | 'UNMATCHED'): void {
        this.activeStatusFilter = filter;
        this.refreshExpectationWidget();
    }

    /**
    * AI: Build / rebuild the server-paginated expectation table.
    * Fixed filters restrict to the DQ statuses and (optionally) the run scope.
    * @widget cn.config.data.quality.expectation.page (entity, list)
    */
    refreshExpectationWidget(): void {
        const filters: FilterCriteria[] = [];

        if (this.activeStatusFilter === 'ALL') {
            // Both DQ statuses: INCOMPLETE_BASE and UNMATCHED.
            filters.push(FilterCriteria.create(
                'e.status', FilterCriteria.cOperatorIn, ['INCOMPLETE_BASE', 'UNMATCHED']
            ));
        } else {
            filters.push(FilterCriteria.create('e.status', FilterCriteria.cOperatorEqual, this.activeStatusFilter));
        }

        if (this.simulationRunId) {
            filters.push(FilterCriteria.create(
                'e.simulationRunId', FilterCriteria.cOperatorEqual, this.simulationRunId
            ));
        }

        this.expectationWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            CnDataQualityPanelComponent.WIDGET_ALIAS,
            'Datenqualitäts-Befunde',
            'cn.CommissionExpectation',
            filters,
            [new Sorting('e.id', false)]
        );
    }

    /**
    * Jump to the related cm.Contract (FR-15: Sprung zum Vertrag).
    * Uses the contract id resolved from the expectation row; PII never leaves IDs (NFR-Sicherheit).
    */
    handleJumpToContract(object: ObjectIdentifierData | ObjectIdentifier): void {
        const data = object as ObjectIdentifierData;
        const contractId = data?.data?.['e.contractId'];
        if (!contractId) {
            return;
        }
        const entry = MvsObjectNavigationEntry.createNavigationEntry(
            'cm.Contract',
            contractId,
            'object',
            '',
            '',
            '',
            MvsObjectNavigationActionEnum.edit
        );
        this.navigationService.navigateTo(entry, 'main');
    }

    /**
    * Coverage percentage (backend counters); returns null when not computable.
    */
    getCoveragePercent(): number | null {
        if (!this.counters || !this.counters.contractsTotal) {
            return null;
        }
        return (this.counters.contractsCovered / this.counters.contractsTotal) * 100;
    }
}
